import useSWR, { keyInterface, mutate } from "swr";
import { useEffect, useState } from "react";

import { FunctionCall } from "../firebaseWrapper";

export interface UseAsyncDataOptions {
  shouldFetchData?: boolean;
  shouldRevalidateData?: boolean;
}

export function getCacheKeyForFunction(
  firebaseFunction: FunctionCall,
  args?: unknown
) {
  return `${firebaseFunction.name}${args ? `-${JSON.stringify(args)}` : ""}`;
}

export function useFirebaseListFunction<T>(
  firebaseFunction: FunctionCall,
  args?: { [s: string]: unknown },
  {
    shouldFetchData = true,
    shouldRevalidateData = true,
  }: UseAsyncDataOptions = {}
) {
  const cacheKey = getCacheKeyForFunction(firebaseFunction, args);
  return useCachedAsyncData<T[]>(
    async () => {
      return (await firebaseFunction.action(args)).data;
    },
    cacheKey,
    { initialData: [], shouldFetchData, shouldRevalidateData }
  );
}

export function useFirebaseFunction<T>(
  firebaseFunction: FunctionCall,
  args?: { [s: string]: unknown },
  {
    shouldFetchData = true,
    shouldRevalidateData = true,
  }: UseAsyncDataOptions = {}
) {
  const cacheKey = getCacheKeyForFunction(firebaseFunction, args);
  return useCachedAsyncData<T | undefined>(
    async () => {
      return (await firebaseFunction.action(args)).data;
    },
    cacheKey,
    { initialData: undefined, shouldFetchData, shouldRevalidateData }
  );
}

export function useCachedAsyncData<T>(
  loadData: () => Promise<T>,
  cacheKey: keyInterface,
  {
    shouldFetchData = true,
    shouldRevalidateData = true,
    initialData,
  }: UseAsyncDataOptions & { initialData: T }
) {
  const { data, error } = useSWR<T>(
    shouldFetchData ? cacheKey : null,
    async () => {
      console.log(`Fetching data for key ${cacheKey}`);
      return loadData();
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: shouldRevalidateData,
    }
  );
  // workaround for the fact SWR doesn't have a neat way to allow
  // a fetch/cache and then no revalidate (see https://github.com/vercel/swr/issues/455)
  useEffect(() => {
    if (!data && !shouldRevalidateData && shouldFetchData) mutate(cacheKey);
  }, [cacheKey, data, shouldFetchData, shouldRevalidateData]);
  return {
    data: data || initialData,
    isLoading: !data && shouldFetchData,
    error,
  };
}

export function useAsyncData<T>(
  loadData: () => Promise<T>,
  { initialData }: { initialData: T }
) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  useEffect(() => {
    let isCancelled = false;
    loadData().then(
      (result) => {
        if (!isCancelled) setData(result);
      },
      (error) => {
        if (!isCancelled) setError(error);
      }
    );
    return () => {
      isCancelled = true;
    };
  }, [loadData]);
  return {
    data: data || initialData,
    isLoading: !data,
    error,
  };
}
